import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { HelmetProvider, Helmet } from "react-helmet-async";

import { AuthProvider } from "@/lib/auth";
import SmoothScroll from "@/components/layout/SmoothScroll";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingBookCall from "@/components/FloatingBookCall";
import CoffeePopup from "@/components/CoffeePopup";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollProgressRing from "@/components/motion/ScrollProgressRing";
import { ORGANIZATION_JSONLD } from "@/components/Seo";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Leadership from "@/pages/Leadership";
import VipFilm from "@/pages/VipFilm";
import EventTech from "@/pages/EventTech";
import SiteSelection from "@/pages/SiteSelection";
import B2bServices from "@/pages/B2bServices";
import HotelTravel from "@/pages/HotelTravel";
import LogisticsPlanning from "@/pages/LogisticsPlanning";
import ProjectManagement from "@/pages/ProjectManagement";
import MeDmc from "@/pages/MeDmc";
import EventMarketing from "@/pages/EventMarketing";
import Government from "@/pages/Government";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AccessibilityPage from "@/pages/AccessibilityPage";
import Speaker from "@/pages/Speaker";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

function PageFrame({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {!isAdminRoute && <Navbar />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <PageFrame>
                <Home />
              </PageFrame>
            }
          />
          <Route
            path="/services"
            element={
              <PageFrame>
                <Services />
              </PageFrame>
            }
          />
          <Route
            path="/about"
            element={
              <PageFrame>
                <About />
              </PageFrame>
            }
          />
          <Route
            path="/contact"
            element={
              <PageFrame>
                <Contact />
              </PageFrame>
            }
          />
          <Route
            path="/leadership"
            element={
              <PageFrame>
                <Leadership />
              </PageFrame>
            }
          />
          <Route
            path="/services/vip-film"
            element={
              <PageFrame>
                <VipFilm />
              </PageFrame>
            }
          />
          <Route
            path="/services/event-tech"
            element={
              <PageFrame>
                <EventTech />
              </PageFrame>
            }
          />
          <Route
            path="/services/site-selection"
            element={
              <PageFrame>
                <SiteSelection />
              </PageFrame>
            }
          />
          <Route
            path="/b2b-services"
            element={
              <PageFrame>
                <B2bServices />
              </PageFrame>
            }
          />
          <Route
            path="/services/hotel-travel"
            element={
              <PageFrame>
                <HotelTravel />
              </PageFrame>
            }
          />
          <Route
            path="/services/logistics-planning"
            element={
              <PageFrame>
                <LogisticsPlanning />
              </PageFrame>
            }
          />
          <Route
            path="/services/project-management"
            element={
              <PageFrame>
                <ProjectManagement />
              </PageFrame>
            }
          />
          <Route
            path="/services/me-dmc"
            element={
              <PageFrame>
                <MeDmc />
              </PageFrame>
            }
          />
          <Route
            path="/services/event-marketing"
            element={
              <PageFrame>
                <EventMarketing />
              </PageFrame>
            }
          />
          <Route
            path="/government"
            element={
              <PageFrame>
                <Government />
              </PageFrame>
            }
          />
          <Route
            path="/privacy"
            element={
              <PageFrame>
                <Privacy />
              </PageFrame>
            }
          />
          <Route
            path="/terms"
            element={
              <PageFrame>
                <Terms />
              </PageFrame>
            }
          />
          <Route
            path="/accessibility"
            element={
              <PageFrame>
                <AccessibilityPage />
              </PageFrame>
            }
          />
          <Route
            path="/speaker"
            element={
              <PageFrame>
                <Speaker />
              </PageFrame>
            }
          />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          {/* 404 catch-all — must be last */}
          <Route
            path="*"
            element={
              <PageFrame>
                <NotFound />
              </PageFrame>
            }
          />
        </Routes>
      </AnimatePresence>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <FloatingBookCall />}
      {!isAdminRoute && <CoffeePopup />}
      {!isAdminRoute && <ScrollProgressRing />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <div className="grain-overlay" aria-hidden="true" />
      <HelmetProvider>
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(ORGANIZATION_JSONLD)}
          </script>
        </Helmet>
        <AuthProvider>
          <BrowserRouter>
            <SmoothScroll>
              <AnimatedRoutes />
            </SmoothScroll>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid rgba(11,37,69,0.14)",
            color: "#0B2545",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: "12px",
            letterSpacing: "0.02em",
          },
        }}
      />
    </div>
  );
}

export default App;
